<template>
    <va-card class="flex mb-4">
        <va-card-title class="page-title">Skill 查看与审批</va-card-title>
        <va-card-content>
            <vxe-form :data="toolbar">
                <vxe-form-item title="关键字">
                    <vxe-input
                        v-model="toolbar.searchKey"
                        placeholder="名称、标识、描述或发布人"
                        clearable
                    />
                </vxe-form-item>
                <vxe-form-item title="应用领域">
                    <vxe-select
                        v-model="toolbar.applicationDomain"
                        :options="applicationDomainOptions"
                        placeholder="全部应用领域"
                        clearable
                        style="width: 110px"
                    />
                </vxe-form-item>
                <vxe-form-item title="状态">
                    <vxe-select
                        v-model="toolbar.status"
                        :options="statusOptions"
                        placeholder="全部状态"
                        clearable
                        style="width: 100px"
                    />
                </vxe-form-item>
                <vxe-form-item title="精选">
                    <vxe-select
                        v-model="toolbar.featured"
                        :options="featuredOptions"
                        style="width: 80px"
                        clearable
                    />
                </vxe-form-item>
                <vxe-form-item>
                    <vxe-button status="primary" @click="search"
                        >查询</vxe-button
                    >
                    <vxe-button @click="reset">重置</vxe-button>
                    <vxe-button
                        v-if="permissions.CanCreateSkill"
                        status="primary"
                        @click="openCreateSkill"
                        >新建 Skill</vxe-button
                    >

                    <vxe-button status="success" @click="exportData"
                        >导出</vxe-button
                    >
                </vxe-form-item>
            </vxe-form>

            <vxe-table
                ref="skillTable"
                border
                stripe
                show-overflow
                :loading="loading"
                :data="tableData"
                :height="tableHeight"
                :row-config="{ height: 36, isHover: true, isCurrent: true }"
                :column-config="{ resizable: true }"
            >
                <vxe-column type="seq" title="序号" width="60" />
                <vxe-column title="操作" width="200" fixed="left">
                    <template #default="{ row }">
                        <vxe-button type="text" @click="openDetail(row)"
                            >查看</vxe-button
                        >
                        <vxe-button
                            v-if="canAudit(row)"
                            status="success"
                            icon="fa fa-check"
                            round
                            @click="openAudit(row)"
                            >{{ reviewStageText(row.ReviewStage) }}</vxe-button
                        >
                    </template>
                </vxe-column>
                <vxe-column
                    v-if="permissions.CanAdminReview"
                    title="下架操作"
                    width="78"
                    fixed="left"
                    align="center"
                >
                    <template #default="{ row }">
                        <vxe-button
                            v-if="
                                row.PublishedVersionStatus === 2 &&
                                permissions.CanUnpublish
                            "
                            type="text"
                            status="warning"
                            @click="openUnpublish(row)"
                            >下架</vxe-button
                        >
                        <span v-else>-</span>
                    </template>
                </vxe-column>
                <vxe-column
                    v-if="permissions.CanAdminReview"
                    title="精选操作"
                    width="92"
                    fixed="left"
                    align="center"
                >
                    <template #default="{ row }">
                        <vxe-button
                            v-if="
                                row.PublishedVersionStatus === 2 &&
                                permissions.CanSetFeatured
                            "
                            type="text"
                            :status="row.IsFeatured ? 'warning' : 'primary'"
                            @click="toggleFeatured(row)"
                            >{{
                                row.IsFeatured ? "取消精选" : "设为精选"
                            }}</vxe-button
                        >
                        <span v-else>-</span>
                    </template>
                </vxe-column>
                <vxe-column
                    field="DisplayName"
                    title="Skill 名称"
                    min-width="170"
                    resizable
                />
                <vxe-column
                    field="PublishTypeText"
                    title="发布类型"
                    width="110"
                >
                    <template #default="{ row }">
                        <span
                            :class="[
                                'publish-type-tag',
                                row.IsVersionUpdate
                                    ? 'version-update'
                                    : 'first-publish',
                            ]"
                        >
                            <strong>{{ row.PublishTypeText }}</strong>
                        </span>
                    </template>
                </vxe-column>
                <vxe-column
                    field="Slug"
                    title="标识"
                    min-width="150"
                    resizable
                />
                <vxe-column field="StatusName" title="状态" width="115">
                    <template #default="{ row }">
                        <button
                            type="button"
                            :class="['status-link', statusClass(row.Status)]"
                            title="查看审核记录"
                            @click="showAuditLogs(row)"
                        >
                            {{ statusText(row.Status) }}
                        </button>
                    </template>
                </vxe-column>
                <vxe-column
                    field="ReviewStageName"
                    title="审批节点"
                    width="120"
                >
                    <template #default="{ row }">{{
                        reviewStageText(row.ReviewStage)
                    }}</template>
                </vxe-column>
                <vxe-column
                    field="ApplicationDomainText"
                    title="应用领域"
                    min-width="120"
                    resizable
                />
                <vxe-column
                    field="VisibilityName"
                    title="可见范围"
                    width="130"
                    resizable
                >
                    <template #default="{ row }">{{
                        visibilityText(row.Visibility)
                    }}</template>
                </vxe-column>
                <vxe-column
                    field="VisibilityTargetText"
                    title="可见对象"
                    min-width="180"
                    show-overflow
                />
                <vxe-column
                    field="OwnerDisplayName"
                    title="发布人"
                    width="120"
                />
                <vxe-column
                    field="DownloadCount"
                    title="安装人数"
                    width="100"
                >
                    <template #default="{ row }">
                        <vxe-button type="text" @click="openInstallDetails(row, 'users')">{{ row.DownloadCount || 0 }}</vxe-button>
                    </template>
                </vxe-column>
                <vxe-column
                    field="DownloadTimes"
                    title="安装次数"
                    width="100"
                >
                    <template #default="{ row }">
                        <vxe-button type="text" @click="openInstallDetails(row, 'events')">{{ row.DownloadTimes || 0 }}</vxe-button>
                    </template>
                </vxe-column>
                <vxe-column field="UsageCount" title="使用次数" width="100" />
                <vxe-column field="IsFeatured" title="精选" width="80">
                    <template #default="{ row }">{{
                        row.IsFeatured ? "是" : "否"
                    }}</template>
                </vxe-column>
                <vxe-column
                    field="PublishedAt"
                    title="发布审核时间"
                    width="170"
                >
                    <template #default="{ row }">{{
                        formatDate(row.PublishedAt)
                    }}</template>
                </vxe-column>
                <vxe-column field="UpdatedAt" title="更新时间" width="170">
                    <template #default="{ row }">{{
                        formatDate(row.UpdatedAt)
                    }}</template>
                </vxe-column>
                <vxe-column field="CreatedAt" title="创建时间" width="170">
                    <template #default="{ row }">{{
                        formatDate(row.CreatedAt)
                    }}</template>
                </vxe-column>
                <vxe-column
                    field="BusinessValue"
                    title="业务价值"
                    min-width="240"
                    show-overflow
                />
                <vxe-column field="Summary" title="描述" min-width="240" />
            </vxe-table>

            <vxe-pager
                background
                v-model:current-page="pager.currentPage"
                v-model:page-size="pager.pageSize"
                :total="pager.totalResult"
                :page-sizes="[10, 20, 50, 100]"
                :layouts="[
                    'PrevPage',
                    'JumpNumber',
                    'NextPage',
                    'Sizes',
                    'FullJump',
                    'Total',
                ]"
                @page-change="loadData"
            />
        </va-card-content>
    </va-card>

    <vxe-modal
        v-model="createVisible"
        title="新建 / 更新 Skill"
        width="760"
        :show-footer="true"
        :mask-closable="false"
    >
        <vxe-form
            ref="createForm"
            :data="createForm"
            :rules="createRules"
            title-width="110"
            title-align="right"
        >
            <vxe-form-item title="ZIP 包" field="file" span="20">
                <vxe-input
                    :model-value="createForm.file ? createForm.file.name : ''"
                    placeholder="请选择 ZIP 包"
                    readonly
                />
            </vxe-form-item>
            <vxe-form-item span="4" title-width="0">
                <vxe-button
                    :loading="archiveParsing"
                    @click="selectCreateArchive"
                    >浏览</vxe-button
                >
            </vxe-form-item>
            <vxe-form-item title="Skill 标识" field="name" span="12">
                <vxe-input
                    v-model="createForm.name"
                    placeholder="小写字母、数字和连字符"
                />
            </vxe-form-item>
            <vxe-form-item title="展示名称" field="displayName" span="12">
                <vxe-input v-model="createForm.displayName" />
            </vxe-form-item>
            <vxe-form-item title="描述" field="description" span="24">
                <vxe-textarea
                    v-model="createForm.description"
                    :autosize="{ minRows: 2, maxRows: 4 }"
                />
            </vxe-form-item>
            <vxe-form-item title="业务价值" field="businessValue" span="24">
                <vxe-textarea
                    v-model="createForm.businessValue"
                    :autosize="{ minRows: 2, maxRows: 4 }"
                />
            </vxe-form-item>
            <vxe-form-item title="发布说明" field="releaseNotes" span="24">
                <vxe-textarea
                    v-model="createForm.releaseNotes"
                    :autosize="{ minRows: 2, maxRows: 4 }"
                />
            </vxe-form-item>
            <vxe-form-item title="标签" span="24">
                <vxe-select
                    v-model="createForm.labels"
                    :options="createOptions.labels"
                    multiple
                    clearable
                    transfer
                />
            </vxe-form-item>
            <vxe-form-item title="可见范围" field="visibility" span="12">
                <vxe-select
                    v-model="createForm.visibility"
                    :options="createOptions.visibilities"
                    transfer
                />
            </vxe-form-item>
            <vxe-form-item
                v-if="createForm.visibility === 'ORG_ONLY'"
                title="可见总部"
                field="orgs"
                span="24"
            >
                <vxe-select
                    v-model="createForm.orgs"
                    :options="createOptions.orgs"
                    multiple
                    clearable
                    transfer
                />
            </vxe-form-item>
            <vxe-form-item
                v-if="createForm.visibility === 'PRIVATE'"
                title="可见人员"
                field="domainAccounts"
                span="20"
            >
                <vxe-input
                    :model-value="selectedCreateUserNames"
                    placeholder="请选择人员"
                    readonly
                />
            </vxe-form-item>
            <vxe-form-item
                v-if="createForm.visibility === 'PRIVATE'"
                span="4"
                title-width="0"
            >
                <vxe-button @click="openCreateUserSelector">选择</vxe-button>
            </vxe-form-item>
        </vxe-form>
        <template #footer>
            <vxe-button @click="createVisible = false">取消</vxe-button>
            <vxe-button
                status="primary"
                :loading="creating"
                @click="saveCreateSkill"
                >保存并立即发布</vxe-button
            >
        </template>
    </vxe-modal>

    <vxe-modal
        title="选择用户"
        v-model="createUserVisible"
        width="640"
        :show-footer="true"
    >
        <vxe-form :data="createUserPager">
            <vxe-form-item title="关键字">
                <vxe-input
                    v-model="createUserPager.searchKey"
                    placeholder="请输入姓名或邮箱"
                    clearable
                    @keyup.enter="loadCreateUsers"
                />
            </vxe-form-item>
            <vxe-form-item>
                <vxe-button @click="loadCreateUsers">查询</vxe-button>
            </vxe-form-item>
            <vxe-form-item span="24" title="已选（双击选择）">
                <span
                    v-for="item in selectedCreateUsers"
                    :key="item.Id"
                    class="selected-user"
                >
                    {{ item.UserName }}
                    <va-icon name="clear" @click="removeCreateUser(item.Id)" />
                </span>
            </vxe-form-item>
        </vxe-form>
        <vxe-table
            ref="createUserTable"
            :data="createUserData"
            height="300"
            highlight-hover-row
            highlight-current-row
            @cell-dblclick="chooseCreateUser"
        >
            <vxe-column type="seq" title="序号" width="50" />
            <vxe-column field="UserName" title="姓名" width="120" />
            <vxe-column field="JobZb" title="总部" width="100" />
            <vxe-column field="JobName" title="部门" width="120" show-overflow />
            <vxe-column field="UserMail" title="邮箱" min-width="200" />
        </vxe-table>
        <vxe-pager
            background
            v-model:current-page="createUserPager.currentPage"
            v-model:page-size="createUserPager.pageSize"
            :total="createUserPager.totalResult"
            @page-change="loadCreateUsers"
            size="small"
        />
        <template #footer>
            <vxe-button status="primary" @click="createUserVisible = false">确定</vxe-button>
        </template>
    </vxe-modal>

    <vxe-modal
        v-model="detailVisible"
        title="Skill 详情"
        class-name="skill-detail-modal"
        width="1040"
        :height="detailModalHeight"
        show-zoom
        resize
    >
        <div v-if="detail" class="detail-wrap">
            <section class="detail-hero">
                <div class="skill-avatar">SK</div>
                <div class="hero-main">
                    <div class="hero-title-row">
                        <h2>{{ detail.DisplayName || detail.Slug }}</h2>
                        <span
                            :class="[
                                'status-badge',
                                statusClass(detail.Status),
                            ]"
                        >
                            <i />{{ statusText(detail.Status) }}
                        </span>
                    </div>
                    <div class="skill-identity">
                        {{ detail.Namespace }}/{{ detail.Slug }}
                        <span class="version-chip"
                            >v{{ detail.Version || "-" }}</span
                        >
                    </div>
                    <p>{{ detail.Summary || "暂无技能描述" }}</p>
                </div>
            </section>

            <div class="detail-columns">
                <section class="info-card">
                    <div class="section-title"><span>01</span>基本信息</div>
                    <div class="info-list">
                        <div class="info-item">
                            <label>发布人</label
                            ><strong>{{
                                (detail.OwnerDisplayName || "-") + (detail.OwnerEmail ? "(" + detail.OwnerEmail + ")" : "")
                            }}</strong>
                        </div>
                        <div class="info-item">
                            <label>可见范围</label
                            ><strong>{{
                                visibilityText(detail.Visibility)
                            }}</strong>
                        </div>
                        <div class="info-item">
                            <label>可见总部</label
                            ><strong>{{ orgText }}</strong>
                        </div>
                        <div class="info-item">
                            <label>可见人员</label
                            ><strong>{{ userText }}</strong>
                        </div>
                        <div class="info-item">
                            <label>安装人数</label
                            ><strong><vxe-button type="text" @click="openInstallDetails(detail, 'users')">{{ detail.DownloadCount || 0 }}</vxe-button></strong>
                        </div>
                        <div class="info-item">
                            <label>安装次数</label
                            ><strong><vxe-button type="text" @click="openInstallDetails(detail, 'events')">{{ detail.DownloadTimes || 0 }}</vxe-button></strong>
                        </div>
                        <div class="info-item">
                            <label>使用次数</label
                            ><strong>{{ detail.UsageCount || 0 }}</strong>
                        </div>
                        <div class="info-item info-item-span-2">
                            <label>业务价值</label
                            ><strong>{{ detail.BusinessValue || "-" }}</strong>
                        </div>
                        <div class="info-item">
                            <label>技能标签</label>
                            <div
                                class="tag-list"
                                v-if="detail.Labels && detail.Labels.length"
                            >
                                <span
                                    v-for="item in detail.Labels"
                                    :key="item.Slug"
                                    class="tag-chip"
                                >
                                    {{ item.DisplayName }}
                                </span>
                            </div>
                            <strong v-else>-</strong>
                        </div>
                    </div>
                </section>
            </div>

            <section class="detail-tabs-card">
                <va-tabs v-model="detailTab" class="detail-tabs">
                    <template #tabs>
                        <va-tab name="skill-md">SKILL.md</va-tab>
                        <va-tab name="versions">版本记录</va-tab>
                    </template>
                </va-tabs>

                <div
                    v-show="detailTab === 'skill-md'"
                    class="document-card tab-panel"
                >
                    <div class="document-head">
                        <div>
                            <strong>SKILL.md</strong>
                            <span>技能说明文档</span>
                        </div>
                        <span class="file-type">MARKDOWN</span>
                    </div>
                    <div class="skill-md markdown-body" v-html="skillMdHtml" />
                </div>

                <div
                    v-show="detailTab === 'versions'"
                    class="tab-panel version-panel"
                >
                    <vxe-table
                        border
                        stripe
                        show-overflow
                        :data="detailVersions"
                        max-height="320"
                    >
                        <vxe-column field="Version" title="版本" width="150" />
                        <vxe-column field="StatusName" title="状态" width="110">
                            <template #default="{ row }">{{
                                statusText(row.Status)
                            }}</template>
                        </vxe-column>
                        <vxe-column
                            field="ReviewStageName"
                            title="审批节点"
                            width="120"
                        >
                            <template #default="{ row }">{{
                                reviewStageText(row.ReviewStage)
                            }}</template>
                        </vxe-column>
                        <vxe-column
                            field="ReleaseNotes"
                            title="发布说明"
                            min-width="220"
                        />
                        <vxe-column
                            field="CreatedByDisplayName"
                            title="提交人"
                            width="110"
                        />
                        <vxe-column
                            field="CreatedAt"
                            title="提交时间"
                            width="170"
                        >
                            <template #default="{ row }">{{
                                formatDate(row.CreatedAt)
                            }}</template>
                        </vxe-column>
                        <vxe-column title="安全审核附件" width="140" align="center">
                            <template #default="{ row }">
                                <vxe-button
                                    v-if="row.HasSecurityAttachment"
                                    type="text"
                                    @click="downloadSecurityAttachment(row)"
                                    >下载</vxe-button
                                >
                                <span v-else>-</span>
                            </template>
                        </vxe-column>
                        <vxe-column title="当前版本" width="110">
                            <template #default="{ row }">
                                <span v-if="row.IsHeadlineVersion">最新</span>
                                <span v-if="row.IsPublishedVersion"
                                    >{{
                                        row.IsHeadlineVersion ? " / " : ""
                                    }}已发布</span
                                >
                            </template>
                        </vxe-column>
                        <vxe-column title="操作" width="100" fixed="right">
                            <template #default="{ row }">
                                <vxe-button
                                    v-if="row.StorageStatus === 1"
                                    type="text"
                                    @click="downloadVersion(row)"
                                    >下载</vxe-button
                                >
                                <span v-else>-</span>
                            </template>
                        </vxe-column>
                    </vxe-table>
                </div>
            </section>
        </div>
    </vxe-modal>

    <vxe-modal
        v-model="auditVisible"
        :title="auditTitle"
        class-name="skill-audit-modal"
        width="820"
        height="96%"
    >
        <div v-if="auditDetail" class="audit-wrap">
            <div class="audit-summary">
                <div class="audit-summary-head">
                    <div class="skill-avatar small">SK</div>
                    <div>
                        <h3>
                            {{ auditDetail.DisplayName || auditDetail.Slug }}
                        </h3>
                        <p>
                            {{ auditDetail.Namespace }}/{{
                                auditDetail.Slug
                            }}@{{ auditDetail.Version }}
                        </p>
                    </div>
                    <span
                        :class="[
                            'status-badge',
                            statusClass(auditDetail.Status),
                        ]"
                    >
                        <i />{{ statusText(auditDetail.Status) }}
                    </span>
                </div>
                <div class="audit-facts">
                    <div>
                        <label>发布人</label
                        ><strong>{{
                            auditDetail.OwnerDisplayName || "-"
                        }}</strong>
                    </div>
                    <div>
                        <label>可见范围</label
                        ><strong>{{
                            visibilityText(auditDetail.Visibility)
                        }}</strong>
                    </div>
                    <div>
                        <label>可见总部</label
                        ><strong>{{ auditOrgText }}</strong>
                    </div>
                    <div>
                        <label>可见人员</label
                        ><strong>{{ auditUserText }}</strong>
                    </div>
                    <div>
                        <label>标签</label><strong>{{ auditLabelText }}</strong>
                    </div>
                    <div class="audit-fact-full">
                        <label>描述</label
                        ><strong>{{ auditDetail.Summary || "-" }}</strong>
                    </div>
                    <div class="audit-fact-full">
                        <label>业务价值</label
                        ><strong>{{ auditDetail.BusinessValue || "-" }}</strong>
                    </div>
                    <div class="audit-fact-full">
                        <label>版本说明</label
                        ><strong class="audit-release-notes">{{
                            auditDetail.ReleaseNotes || "-"
                        }}</strong>
                    </div>
                    <div class="audit-fact-full">
                        <label>Skill 附件</label>
                        <vxe-button
                            v-if="
                                auditDetail.StorageStatus === 1 &&
                                auditDetail.HeadlineVersionId
                            "
                            type="text"
                            icon="fa fa-download"
                            @click="downloadAuditAttachment"
                            >{{
                                auditDetail.ArchiveFileName || "下载附件"
                            }}</vxe-button
                        >
                        <strong v-else>附件暂不可下载</strong>
                    </div>
                </div>
            </div>
            <label class="opinion-label">审核意见</label>
            <vxe-textarea
                v-model="auditRemark"
                style="width: 100%"
                :maxlength="1000"
                show-word-count
                :rows="4"
                placeholder="请输入审核意见；驳回时必填"
            />
            <div v-if="auditDetail.ReviewStage === 1" class="security-attachment-field">
                <label class="opinion-label">安全审核附件（必填，单文件不超过 20MB）</label>
                <div>
                    <vxe-button icon="fa fa-upload" @click="selectSecurityAttachment">选择附件</vxe-button>
                    <span>{{ securityAttachment ? securityAttachment.name : "尚未选择附件" }}</span>
                </div>
                <small>支持 PDF、Word、Excel、JPG、PNG、ZIP、RAR</small>
            </div>
            <div class="modal-actions">
                <vxe-button
                    status="danger"
                    icon="fa fa-reply"
                    :loading="submitting"
                    @click="reject"
                    >驳回</vxe-button
                >
                <vxe-button
                    status="success"
                    icon="fa fa-check"
                    :loading="submitting"
                    @click="approve"
                    >通过</vxe-button
                >
            </div>
        </div>
    </vxe-modal>

    <vxe-modal
        v-model="unpublishVisible"
        title="下架 Skill"
        width="560"
        height="330"
    >
        <div class="unpublish-wrap">
            <p>
                确定下架“{{
                    unpublishRow &&
                    (unpublishRow.DisplayName || unpublishRow.Slug)
                }}”吗？
            </p>
            <label class="opinion-label">下架原因</label>
            <vxe-textarea
                v-model="unpublishRemark"
                style="width: 100%"
                :maxlength="1000"
                show-word-count
                :rows="5"
                placeholder="请输入具体下架原因"
            />
            <div class="modal-actions">
                <vxe-button @click="unpublishVisible = false">取消</vxe-button>
                <vxe-button
                    status="warning"
                    :loading="submitting"
                    @click="submitUnpublish"
                    >确认下架</vxe-button
                >
            </div>
        </div>
    </vxe-modal>

    <vxe-modal
        v-model="auditLogsVisible"
        title="审核记录"
        width="620"
        height="600"
    >
        <div class="audit-log-wrap">
            <div v-if="!auditLogs.length" class="empty-log">暂无审核记录</div>
            <div
                v-for="item in auditLogs"
                :key="item.Id"
                class="audit-log-item"
            >
                <div :class="['log-dot', statusClass(item.ToStatus)]" />
                <div class="log-content">
                    <div class="log-head">
                        <strong>{{ item.ActionName }}</strong>
                        <time>{{ formatDate(item.CreatedAt) }}</time>
                    </div>
                    <div class="log-meta">
                        {{
                            item.OperatorDisplayName ||
                            item.OperatorDomainAccount ||
                            "-"
                        }}
                        <span v-if="item.Version">版本 {{ item.Version }}</span>
                        <span
                            >{{ statusText(item.FromStatus) }} →
                            {{ statusText(item.ToStatus) }}</span
                        >
                    </div>
                    <p>{{ item.Remark || "无审核意见" }}</p>
                </div>
            </div>
        </div>
    </vxe-modal>

    <vxe-modal v-model="installDetailsVisible" :title="installDetailsTitle" width="860" height="600">
        <div class="install-detail-note">
            明细仅包含日志功能上线后可追溯的安装记录；历史迁移数据未生成明细，因此可能与列表总数不一致。
        </div>
        <vxe-table border stripe :loading="installDetailsLoading" :data="installDetailsData" height="440">
            <vxe-column type="seq" title="序号" width="60" />
            <vxe-column field="UserDisplayName" title="用户" min-width="130" />
            <vxe-column field="UserIdentity" title="用户标识" min-width="160" />
            <template v-if="installDetailsMode === 'users'">
                <vxe-column field="FirstInstalledAt" title="首次安装时间" width="170">
                    <template #default="{ row }">{{ formatDate(row.FirstInstalledAt) }}</template>
                </vxe-column>
                <vxe-column field="LatestInstalledAt" title="最近安装时间" width="170">
                    <template #default="{ row }">{{ formatDate(row.LatestInstalledAt) }}</template>
                </vxe-column>
                <vxe-column field="InstallCount" title="安装次数" width="100" />
            </template>
            <template v-else>
                <vxe-column field="Version" title="Skill 版本" width="140" />
                <vxe-column field="InstalledAt" title="安装时间" width="170">
                    <template #default="{ row }">{{ formatDate(row.InstalledAt) }}</template>
                </vxe-column>
            </template>
        </vxe-table>
        <vxe-pager background v-model:current-page="installDetailsPager.currentPage"
            v-model:page-size="installDetailsPager.pageSize" :total="installDetailsPager.totalResult"
            :page-sizes="[10, 20, 50, 100]" @page-change="loadInstallDetails" />
    </vxe-modal>
</template>

<script>
import Qs from "qs";
import VXETable from "vxe-table";
import MarkdownIt from "markdown-it/dist/markdown-it.js";
import DOMPurify from "dompurify";
import JSZip from "jszip";
import yaml from "js-yaml";

const markdownRenderer = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: true,
});

const normalizeFrontMatter = (source) =>
    source.replace(/^\uFEFF?\s*---\s*\r?\n[\s\S]*?\r?\n---\s*(?:\r?\n|$)/, "");

export default {
    name: "SkillManage",
    data() {
        return {
            loading: false,
            submitting: false,
            tableHeight: 0,
            detailModalHeight: Math.max(600, window.innerHeight - 50),
            tableData: [],
            creating: false,
            archiveParsing: false,
            createVisible: false,
            createForm: {
                name: "",
                displayName: "",
                description: "",
                businessValue: "",
                releaseNotes: "",
                labels: [],
                visibility: "PUBLIC",
                orgs: [],
                domainAccounts: "",
                file: null,
            },
            createOptions: { labels: [], visibilities: [], orgs: [] },
            createRules: {
                file: [{ required: true, message: "请选择 ZIP 包" }],
                name: [{ required: true, message: "请填写 Skill 标识" }],
                displayName: [{ required: true, message: "请填写展示名称" }],
                description: [{ required: true, message: "请填写描述" }],
                businessValue: [{ required: true, message: "请填写业务价值" }],
                releaseNotes: [{ required: true, message: "请填写发布说明" }],
                visibility: [{ required: true, message: "请选择可见范围" }],
                orgs: [{ required: true, message: "请选择可见总部" }],
                domainAccounts: [
                    { required: true, message: "请选择可见人员" },
                ],
            },
            createUserVisible: false,
            createUserData: [],
            selectedCreateUsers: [],
            createUserPager: {
                currentPage: 1,
                pageSize: 10,
                totalResult: 0,
                searchKey: "",
            },
            toolbar: { searchKey: "", status: "", featured: "", applicationDomain: "" },
            applicationDomainOptions: [],
            pager: { currentPage: 1, pageSize: 10, totalResult: 0 },
            statusOptions: [
                { label: "待审批", value: "1" },
                { label: "已发布", value: "2" },
                { label: "已驳回", value: "3" },
                { label: "已下架", value: "4" },
                { label: "已归档", value: "5" },
            ],
            featuredOptions: [
                { label: "全部", value: "" },
                { label: "是", value: "true" },
                { label: "否", value: "false" },
            ],
            detailVisible: false,
            detail: null,
            detailVersions: [],
            detailTab: "skill-md",
            permissions: {
                CanSecurityReview: false,
                CanAdminReview: false,
                CanUnpublish: false,
                CanSetFeatured: false,
                CanCreateSkill: false,
            },
            auditVisible: false,
            auditRow: null,
            auditDetail: null,
            auditRemark: "审核通过",
            securityAttachment: null,
            auditLogsVisible: false,
            auditLogs: [],
            installDetailsVisible: false,
            installDetailsLoading: false,
            installDetailsMode: "users",
            installDetailsSkill: null,
            installDetailsData: [],
            installDetailsPager: { currentPage: 1, pageSize: 10, totalResult: 0 },
            unpublishVisible: false,
            unpublishRow: null,
            unpublishRemark: "",
        };
    },
    computed: {
        installDetailsTitle() {
            const metric = this.installDetailsMode === "users" ? "安装人数明细" : "安装次数明细";
            const name = this.installDetailsSkill && (this.installDetailsSkill.DisplayName || this.installDetailsSkill.Slug);
            return name ? `${metric} - ${name}` : metric;
        },
        selectedCreateUserNames() {
            return this.selectedCreateUsers
                .map((item) => item.UserName)
                .join("；");
        },
        skillMdHtml() {
            const source = this.detail && this.detail.SkillMdContent;
            if (!source) {
                return '<p class="markdown-empty">暂无内容</p>';
            }
            return DOMPurify.sanitize(
                markdownRenderer.render(normalizeFrontMatter(source))
            );
        },
        labelText() {
            const labels = this.detail && this.detail.Labels;
            return labels && labels.length
                ? labels.map((item) => item.DisplayName).join("、")
                : "-";
        },
        orgText() {
            const items = this.detail && this.detail.Orgs;
            return items && items.length
                ? items.map((item) => item.DisplayName).join("、")
                : "-";
        },
        userText() {
            const items = this.detail && this.detail.DomainAccounts;
            return items && items.length
                ? items
                      .map(
                          (item) =>
                              `${item.DisplayName || item.DomainAccount}（${
                                  item.DomainAccount
                              }）`
                      )
                      .join("、")
                : "-";
        },
        auditLabelText() {
            const items = this.auditDetail && this.auditDetail.Labels;
            return items && items.length
                ? items.map((item) => item.DisplayName).join("、")
                : "-";
        },
        auditOrgText() {
            const items = this.auditDetail && this.auditDetail.Orgs;
            return items && items.length
                ? items.map((item) => item.DisplayName).join("、")
                : "-";
        },
        auditUserText() {
            const items = this.auditDetail && this.auditDetail.DomainAccounts;
            return items && items.length
                ? items
                      .map((item) => item.DisplayName || item.DomainAccount)
                      .join("、")
                : "-";
        },
        auditTitle() {
            return `Skill 审批 - ${this.reviewStageText(
                this.auditDetail && this.auditDetail.ReviewStage
            )}`;
        },
    },
    mounted() {
        this.loadPermissions();
        this.loadApplicationDomainOptions();
        this.loadData();
        window.addEventListener("resize", this.resizeTable);
    },
    unmounted() {
        window.removeEventListener("resize", this.resizeTable);
    },
    methods: {
        loadApplicationDomainOptions() {
            this.$api.skillManageAPI.getSkillApplicationDomainOptions()
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    this.applicationDomainOptions = (res.data.Data || []).map((item) => ({
                        value: item.Slug,
                        label: item.DisplayName,
                    }));
                })
                .catch(this.showError);
        },
        openInstallDetails(row, mode) {
            this.installDetailsSkill = row;
            this.installDetailsMode = mode;
            this.installDetailsData = [];
            this.installDetailsPager.currentPage = 1;
            this.installDetailsPager.totalResult = 0;
            this.installDetailsVisible = true;
            this.loadInstallDetails();
        },
        loadInstallDetails() {
            if (!this.installDetailsSkill) {
                return;
            }
            this.installDetailsLoading = true;
            this.$api.skillManageAPI.getSkillInstallDetails(Qs.stringify({
                skillId: this.installDetailsSkill.Id,
                mode: this.installDetailsMode,
                currentPage: this.installDetailsPager.currentPage,
                pageSize: this.installDetailsPager.pageSize,
            })).then((res) => {
                if (!res.data.Success) {
                    throw new Error(res.data.Message);
                }
                const data = res.data.Data || {};
                this.installDetailsData = data.dataTable || [];
                this.installDetailsPager.totalResult = data.totalResult || 0;
            }).catch(this.showError).finally(() => {
                this.installDetailsLoading = false;
            });
        },
        resetCreateForm() {
            this.createForm = {
                name: "",
                displayName: "",
                description: "",
                businessValue: "",
                releaseNotes: "",
                labels: [],
                visibility: "PUBLIC",
                orgs: [],
                domainAccounts: "",
                file: null,
            };
            this.selectedCreateUsers = [];
        },
        openCreateSkill() {
            this.resetCreateForm();
            this.$api.skillManageAPI
                .getSkillCreateOptions()
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    const data = res.data.Data || {};
                    this.createOptions = {
                        labels: (data.Labels || []).map((item) => ({
                            label: item.DisplayName,
                            value: item.Slug,
                        })),
                        visibilities: (data.Visibilities || []).map((item) => ({
                            label: item.DisplayName,
                            value: item.Slug,
                        })),
                        orgs: (data.Orgs || []).map((item) => ({
                            label: item.DisplayName,
                            value: item.Slug,
                        })),
                    };
                    this.createVisible = true;
                })
                .catch(this.showError);
        },
        async selectCreateArchive() {
            const { files } = await VXETable.readFile({
                types: ["zip"],
                multiple: false,
            });
            if (!files || !files.length) {return;}

            const file = files[0];
            this.archiveParsing = true;
            try {
                const archive = await JSZip.loadAsync(file);
                const skillMdEntry = archive.file("SKILL.md");
                if (!skillMdEntry || skillMdEntry.dir) {
                    throw new Error("技能包不正确：ZIP 根目录缺少 SKILL.md 文件");
                }

                const skillMd = await skillMdEntry.async("string");
                const frontMatter = skillMd.match(
                    /^\uFEFF?\s*---\s*\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/
                );
                let manifest = {};
                if (frontMatter) {
                    try {
                        manifest = yaml.safeLoad(frontMatter[1]) || {};
                    } catch {
                        manifest = {};
                    }
                }

                const textValue = (value) =>
                    typeof value === "string" ? value.trim() : "";
                const metadata =
                    manifest.metadata && typeof manifest.metadata === "object"
                        ? manifest.metadata
                        : {};

                this.createForm.file = file;
                this.createForm.name = textValue(manifest.name);
                this.createForm.description = textValue(manifest.description);
                this.createForm.displayName = textValue(metadata["display-name"]);
            } catch (error) {
                this.createForm.file = null;
                this.createForm.name = "";
                this.createForm.description = "";
                this.createForm.displayName = "";
                VXETable.modal.message({
                    status: "error",
                    content:
                        error.message && error.message.startsWith("技能包不正确")
                            ? error.message
                            : "技能包不正确：ZIP 或 SKILL.md 格式无法解析",
                });
            } finally {
                this.archiveParsing = false;
            }
        },
        openCreateUserSelector() {
            this.createUserPager.currentPage = 1;
            this.loadCreateUsers();
            this.createUserVisible = true;
        },
        loadCreateUsers() {
            const params = {
                uid: this.$store.state.userID,
                email: this.$store.state.email,
                currentPage: this.createUserPager.currentPage,
                pageSize: this.createUserPager.pageSize,
                searchKey: [this.createUserPager.searchKey],
            };
            this.$api.systemManageAPI
                .getUserList(Qs.stringify(params))
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    const data = res.data.Data || {};
                    this.createUserData = data.dataTable || [];
                    this.createUserPager.currentPage = data.currentPage;
                    this.createUserPager.pageSize = data.pageSize;
                    this.createUserPager.totalResult = data.totalResult;
                })
                .catch(this.showError);
        },
        chooseCreateUser({ row }) {
            if (this.selectedCreateUsers.some((item) => item.Id === row.Id)) {
                VXETable.modal.message({
                    content: "该人员已选择",
                    status: "warning",
                });
                return;
            }
            this.selectedCreateUsers.push(row);
            this.createForm.domainAccounts = this.selectedCreateUsers
                .map((item) => item.uid)
                .filter(Boolean)
                .join(",");
        },
        removeCreateUser(id) {
            this.selectedCreateUsers = this.selectedCreateUsers.filter(
                (item) => item.Id !== id
            );
            this.createForm.domainAccounts = this.selectedCreateUsers
                .map((item) => item.uid)
                .filter(Boolean)
                .join(",");
        },
        async saveCreateSkill() {
            const form = this.createForm;
            const formErrMap = await this.$refs.createForm.validate();
            if (formErrMap) {return;}

            const data = new FormData();
            data.append("name", form.name.trim());
            data.append("displayName", form.displayName.trim());
            data.append("description", form.description.trim());
            data.append("businessValue", form.businessValue.trim());
            data.append("releaseNotes", form.releaseNotes.trim());
            data.append("labels", form.labels.join(","));
            data.append("visibility", form.visibility);
            data.append(
                "orgs",
                form.visibility === "ORG_ONLY" ? form.orgs.join(",") : ""
            );
            data.append(
                "domainAccounts",
                form.visibility === "PRIVATE" ? form.domainAccounts.trim() : ""
            );
            data.append("file", form.file);
            this.creating = true;
            this.$api.skillManageAPI
                .createSkill(data)
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    VXETable.modal.message({
                        status: "success",
                        content: res.data.Message || "Skill 保存成功",
                    });
                    this.createVisible = false;
                    this.search();
                })
                .catch(this.showError)
                .finally(() => {
                    this.creating = false;
                });
        },
        loadPermissions() {
            this.$api.skillManageAPI
                .getSkillManagePermissions()
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    this.permissions = {
                        ...this.permissions,
                        ...(res.data.Data || {}),
                    };
                })
                .catch(this.showError);
        },
        resizeTable() {
            this.tableHeight = this.$calTableHeight(this.tableData.length);
            this.detailModalHeight = Math.max(320, window.innerHeight - 96);
        },
        search() {
            this.pager.currentPage = 1;
            this.loadData();
        },
        reset() {
            this.toolbar.searchKey = "";
            this.toolbar.status = "";
            this.toolbar.featured = "";
            this.toolbar.applicationDomain = "";
            this.search();
        },
        exportData() {
            const table = this.$refs.skillTable;
            if (!table) {
                return;
            }
            table.exportData({
                type: "csv",
                filename: `Skill查看与审批_${new Date()
                    .toISOString()
                    .replace(/[-:T]/g, "")
                    .slice(0, 14)}`,
                columnFilterMethod: ({ column }) => Boolean(column.field),
            });
        },
        loadData() {
            this.loading = true;
            const params = {
                currentPage: this.pager.currentPage,
                pageSize: this.pager.pageSize,
                searchKey: this.toolbar.searchKey,
                status: this.toolbar.status,
                featured: this.toolbar.featured,
                applicationDomain: this.toolbar.applicationDomain,
            };
            this.$api.skillManageAPI
                .getSkillList(Qs.stringify(params))
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    const data = res.data.Data;
                    this.tableData = (data.dataTable || []).map((row) => ({
                        ...row,
                        PublishTypeText: row.IsVersionUpdate
                            ? "版本更新"
                            : "首次发布",
                    }));
                    this.tableHeight = this.$calTableHeight(
                        this.tableData.length
                    );
                    this.pager.totalResult = data.totalResult || 0;
                })
                .catch(this.showError)
                .finally(() => {
                    this.loading = false;
                });
        },
        openDetail(row) {
            this.detailTab = "skill-md";
            const params = Qs.stringify({ skillId: row.Id });
            Promise.all([
                this.$api.skillManageAPI.getSkillDetail(params),
                this.$api.skillManageAPI.getSkillVersions(params),
            ])
                .then(([detailRes, versionsRes]) => {
                    if (!detailRes.data.Success) {
                        throw new Error(detailRes.data.Message);
                    }
                    if (!versionsRes.data.Success) {
                        throw new Error(versionsRes.data.Message);
                    }
                    this.detail = detailRes.data.Data;
                    this.detailVersions = versionsRes.data.Data || [];
                    this.detailVisible = true;
                })
                .catch(this.showError);
        },
        openAudit(row) {
            this.auditRow = row;
            this.auditRemark = "审核通过";
            this.securityAttachment = null;
            this.$api.skillManageAPI
                .getSkillDetail(Qs.stringify({ skillId: row.Id }))
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    this.auditDetail = res.data.Data;
                    this.auditVisible = true;
                })
                .catch(this.showError);
        },
        canAudit(row) {
            if (!row || row.Status !== 1) {
                return false;
            }
            if (row.ReviewStage === 1) {
                return this.permissions.CanSecurityReview;
            }
            if (row.ReviewStage === 2) {
                return this.permissions.CanAdminReview;
            }
            return false;
        },
        showAuditLogs(row) {
            this.$api.skillManageAPI
                .getSkillAuditLogs(Qs.stringify({ skillId: row.Id }))
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    this.auditLogs = res.data.Data || [];
                    this.auditLogsVisible = true;
                })
                .catch(this.showError);
        },
        async approve() {
            if (!this.validateSecurityAttachment()) {
                return;
            }
            const result = await VXETable.modal.confirm(
                "确定审批通过该 Skill 吗？"
            );
            if (result !== "confirm") {
                return;
            }
            this.submitAudit(
                this.auditDetail.ReviewStage === 1
                    ? "securityApproveSkill"
                    : "adminApproveSkill",
                "审批通过"
            );
        },
        async reject() {
            if (!this.auditRemark.trim() || this.auditRemark === "审核通过") {
                VXETable.modal.message({
                    content: "驳回时请填写具体原因",
                    status: "warning",
                });
                return;
            }
            if (!this.validateSecurityAttachment()) {
                return;
            }
            const result = await VXETable.modal.confirm(
                "确定驳回该 Skill 吗？"
            );
            if (result !== "confirm") {
                return;
            }
            this.submitAudit(
                this.auditDetail.ReviewStage === 1
                    ? "securityRejectSkill"
                    : "adminRejectSkill",
                "驳回成功"
            );
        },
        submitAudit(method, message) {
            if (this.submitting) {
                return;
            }
            this.submitting = true;
            const values = {
                skillId: this.auditRow.Id,
                userId: this.$store.state.Id,
                remark: this.auditRemark,
            };
            let params = Qs.stringify(values);
            if (this.auditDetail.ReviewStage === 1) {
                params = new FormData();
                Object.keys(values).forEach((key) =>
                    params.append(key, values[key])
                );
                params.append("attachment", this.securityAttachment);
            }
            this.$api.skillManageAPI[method](params)
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    VXETable.modal.message({
                        content: message,
                        status: "success",
                    });
                    this.auditVisible = false;
                    this.loadData();
                })
                .catch((error) => {
                    this.showError(error);
                    if (error.response && error.response.status === 409) {
                        this.auditVisible = false;
                        this.loadData();
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        async selectSecurityAttachment() {
            const { files } = await VXETable.readFile({
                types: ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "zip", "rar"],
                multiple: false,
            });
            if (!files || !files.length) {
                return;
            }
            this.securityAttachment = files[0];
            this.validateSecurityAttachment();
        },
        validateSecurityAttachment() {
            if (!this.auditDetail || this.auditDetail.ReviewStage !== 1) {
                return true;
            }
            const file = this.securityAttachment;
            const allowed = /\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png|zip|rar)$/i;
            let content = "";
            if (!file) {
                content = "安全科审核必须上传附件";
            } else if (!allowed.test(file.name || "")) {
                content = "附件格式仅支持 PDF、Word、Excel、JPG、PNG、ZIP、RAR";
            } else if (file.size <= 0 || file.size > 20 * 1024 * 1024) {
                content = "附件大小必须大于 0 且不超过 20MB";
            }
            if (!content) {
                return true;
            }
            this.securityAttachment = null;
            VXETable.modal.message({ content, status: "warning" });
            return false;
        },
        openUnpublish(row) {
            this.unpublishRow = row;
            this.unpublishRemark = "";
            this.unpublishVisible = true;
        },
        submitUnpublish() {
            if (!this.unpublishRemark.trim()) {
                VXETable.modal.message({
                    content: "请填写具体下架原因",
                    status: "warning",
                });
                return;
            }
            if (this.submitting) {
                return;
            }
            this.submitting = true;
            this.$api.skillManageAPI
                .unpublishSkill(
                    Qs.stringify({
                        skillId: this.unpublishRow.Id,
                        remark: this.unpublishRemark.trim(),
                    })
                )
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    VXETable.modal.message({
                        content: "下架成功",
                        status: "success",
                    });
                    this.unpublishVisible = false;
                    this.loadData();
                })
                .catch(this.showError)
                .finally(() => {
                    this.submitting = false;
                });
        },
        async toggleFeatured(row) {
            const featured = !row.IsFeatured;
            const result = await VXETable.modal.confirm(
                `确定${featured ? "设置" : "取消"}精选“${
                    row.DisplayName || row.Slug
                }”吗？`
            );
            if (result !== "confirm") {
                return;
            }
            this.$api.skillManageAPI
                .setSkillFeatured(
                    Qs.stringify({ skillId: row.Id, featured, sort: 0 })
                )
                .then((res) => {
                    if (!res.data.Success) {
                        throw new Error(res.data.Message);
                    }
                    VXETable.modal.message({
                        content: res.data.Message,
                        status: "success",
                    });
                    this.loadData();
                })
                .catch(this.showError);
        },
        downloadVersion(row) {
            this.$api.skillManageAPI
                .downloadSkillVersion(row.VersionId)
                .then((res) => {
                    const blob = new Blob([res.data], {
                        type: "application/zip",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    const slug =
                        row.Slug ||
                        (this.detail && this.detail.Slug) ||
                        (this.auditDetail && this.auditDetail.Slug) ||
                        "skill";
                    link.download =
                        row.ArchiveFileName || `${slug}-${row.Version}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
                .catch(this.showError);
        },
        downloadSecurityAttachment(row) {
            this.$api.skillManageAPI
                .downloadSecurityAttachment(row.VersionId)
                .then((res) => {
                    const blob = new Blob([res.data], {
                        type: res.headers["content-type"] || "application/octet-stream",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download =
                        row.SecurityAttachmentFileName ||
                        "security-review-attachment";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
                .catch(this.showError);
        },
        downloadAuditAttachment() {
            if (!this.auditDetail || !this.auditDetail.HeadlineVersionId) {
                return;
            }
            this.downloadVersion({
                VersionId: this.auditDetail.HeadlineVersionId,
                ArchiveFileName: this.auditDetail.ArchiveFileName,
                Slug: this.auditDetail.Slug,
                Version: this.auditDetail.Version,
            });
        },
        showError(error) {
            const message =
                error.response?.data?.Message || error.message || "操作失败";
            VXETable.modal.message({ content: message, status: "error" });
        },
        statusText(status) {
            return (
                {
                    1: "待审批",
                    2: "已发布",
                    3: "已驳回",
                    4: "已下架",
                    5: "已归档",
                }[status] || "未知"
            );
        },
        statusClass(status) {
            return (
                {
                    1: "reviewing",
                    2: "published",
                    3: "rejected",
                    4: "unpublished",
                    5: "archived",
                }[status] || ""
            );
        },
        visibilityText(value) {
            return { 1: "个人", 2: "总部", 3: "全部" }[value] || "未知";
        },
        reviewStageText(value) {
            return (
                { 1: "安全科审核", 2: "管理员审核", 3: "审批完成" }[value] ||
                "-"
            );
        },
        formatDate(value) {
            if (!value) {
                return "-";
            }
            const text = String(value);
            const date = new Date(
                /[zZ]$|[+-]\d{2}:\d{2}$/.test(text) ? text : `${text}Z`
            );
            if (Number.isNaN(date.getTime())) {
                return text.replace("T", " ").replace(/Z$/, "").slice(0, 19);
            }
            const pad = (part) => String(part).padStart(2, "0");
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
                date.getDate()
            )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
                date.getSeconds()
            )}`;
        },
    },
};
</script>

<style scoped>
.page-title {
    height: 36px;
    padding: 8px 20px;
}
.status {
    font-weight: 600;
}
.status-link {
    padding: 2px 4px;
    border: 0;
    background: transparent;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 4px;
}
.reviewing {
    color: #e6a23c;
}
.published {
    color: #42b983;
}
.rejected {
    color: #f56c6c;
}
.unpublished {
    color: #909399;
}
.archived {
    color: #475467;
}
.publish-type-tag {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border: 1px solid;
    border-radius: 12px;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
}
.publish-type-tag strong {
    font-weight: 600;
}
.publish-type-tag.first-publish {
    border-color: #91caff;
    background: #e6f4ff;
    color: #1677ff;
}
.publish-type-tag.version-update {
    border-color: #d3adf7;
    background: #f9f0ff;
    color: #722ed1;
}
.detail-wrap {
    box-sizing: border-box;
    height: 100%;
    padding: 4px 20px 24px;
    overflow-x: hidden;
    overflow-y: auto;
    color: #172033;
    font-size: 15px;
    line-height: 1.7;
}
:deep(.skill-detail-modal .vxe-modal--box) {
    max-height: calc(100vh - 96px);
}
:deep(.skill-detail-modal .vxe-modal--content) {
    box-sizing: border-box;
    height: 100%;
    overflow: hidden;
}
.audit-wrap {
    display: flex;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    height: 100%;
    flex-direction: column;
    padding: 2px 18px 10px;
    overflow: hidden;
}
:deep(.skill-audit-modal .vxe-modal--box) {
    max-height: calc(100vh - 32px);
}
:deep(.skill-audit-modal .vxe-modal--content) {
    box-sizing: border-box;
    height: 100%;
    overflow: hidden;
}
.detail-hero {
    display: flex;
    gap: 18px;
    padding: 20px 22px;
    border: 1px solid #e6eaf0;
    border-radius: 12px;
    background: linear-gradient(135deg, #f7faff 0%, #fff 58%, #f8fbff 100%);
    box-shadow: 0 5px 18px rgba(31, 55, 89, 0.06);
}
.skill-avatar {
    display: flex;
    flex: 0 0 54px;
    width: 54px;
    height: 54px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: linear-gradient(145deg, #1769e0, #4c91f3);
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 7px 16px rgba(23, 105, 224, 0.24);
}
.hero-main {
    min-width: 0;
    flex: 1;
}
.hero-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hero-title-row h2 {
    margin: 0;
    color: #172033;
    font-size: 20px;
    font-weight: 650;
    line-height: 28px;
}
.skill-identity {
    margin-top: 4px;
    color: #667085;
    font-family: Consolas, "SFMono-Regular", monospace;
    font-size: 12px;
}
.version-chip {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 7px;
    border: 1px solid #d7e3f4;
    border-radius: 10px;
    background: #fff;
    color: #3974bc;
}
.hero-main p {
    margin: 10px 0 0;
    color: #5f6b7a;
    font-size: 15px;
    line-height: 24px;
}
.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}
.status-badge i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}
.status-badge.reviewing {
    background: #fff6e6;
    color: #b86e00;
}
.status-badge.published {
    background: #eaf8f0;
    color: #16834b;
}
.status-badge.rejected {
    background: #fff0f0;
    color: #c53b3b;
}
.status-badge.unpublished {
    background: #f0f2f5;
    color: #667085;
}
.status-badge.archived {
    background: #e4e7ec;
    color: #344054;
}
.detail-columns {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-top: 14px;
}
.info-card,
.document-card {
    border: 1px solid #e6eaf0;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(31, 55, 89, 0.035);
}
.info-card {
    padding: 14px 20px;
}
.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 11px;
    color: #25324b;
    font-size: 14px;
    font-weight: 650;
}
.section-title span {
    display: inline-flex;
    width: 25px;
    height: 20px;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: #edf4ff;
    color: #2875d2;
    font-size: 10px;
    letter-spacing: 0.5px;
}
.info-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px 22px;
}
.info-item {
    min-width: 0;
}
.info-item label,
.archive-line label {
    display: block;
    margin-bottom: 5px;
    color: #8a94a6;
    font-size: 14px;
}
.info-item strong {
    color: #344054;
    font-size: 15px;
    font-weight: 500;
    line-height: 23px;
}
.info-item-span-2 {
    grid-column: span 2;
}
.tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.tag-chip {
    padding: 3px 9px;
    border: 1px solid #cfe0f7;
    border-radius: 12px;
    background: #f3f8ff;
    color: #306cad;
    font-size: 14px;
}
.archive-card {
    margin-top: 14px;
}
.detail-tabs-card {
    margin-top: 14px;
    overflow: hidden;
    border: 1px solid #e6eaf0;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(31, 55, 89, 0.035);
}
.detail-tabs {
    padding: 0 16px;
    border-bottom: 1px solid #e6eaf0;
}
.tab-panel {
    margin: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
}
.version-panel {
    padding: 14px 16px 16px;
}
.archive-line {
    display: grid;
    grid-template-columns: 82px 1fr;
    align-items: start;
    padding: 8px 0;
    border-top: 1px solid #f0f2f5;
}
.archive-line:first-of-type {
    border-top: 0;
}
.archive-line label {
    margin: 0;
}
.archive-line span,
.archive-line code {
    min-width: 0;
    color: #344054;
    font-size: 15px;
    overflow-wrap: anywhere;
}
.archive-line code {
    padding: 0;
    background: transparent;
    font-family: Consolas, "SFMono-Regular", monospace;
}
.document-card {
    margin-top: 14px;
    overflow: hidden;
}
.document-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 17px;
    border-bottom: 1px solid #e6eaf0;
    background: #f8fafc;
}
.document-head strong {
    color: #25324b;
    font-family: Consolas, "SFMono-Regular", monospace;
    font-size: 14px;
}
.document-head div span {
    margin-left: 10px;
    color: #98a2b3;
    font-size: 12px;
}
.file-type {
    padding: 3px 7px;
    border-radius: 4px;
    background: #e9eef5;
    color: #667085;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.6px;
}
.skill-md {
    box-sizing: border-box;
    height: 300px;
    padding: 14px 20px 18px;
    overflow: auto;
    background: #fbfcfe;
    color: #344054;
    font-size: 14px;
    line-height: 1.65;
    word-break: break-word;
}
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
    margin: 0.9em 0 0.45em;
    color: #172033;
    font-weight: 650;
    line-height: 1.35;
}
.markdown-body :deep(h1) {
    padding-bottom: 0.3em;
    border-bottom: 1px solid #dfe5ed;
    font-size: 22px;
}
.markdown-body :deep(h2) {
    padding-bottom: 0.25em;
    border-bottom: 1px solid #e6eaf0;
    font-size: 19px;
}
.markdown-body :deep(h3) {
    font-size: 17px;
}
.markdown-body :deep(h4) {
    font-size: 15px;
}
.markdown-body :deep(h1:first-child),
.markdown-body :deep(h2:first-child),
.markdown-body :deep(h3:first-child),
.markdown-body :deep(p:first-child) {
    margin-top: 0;
}
.markdown-body :deep(p) {
    margin: 0 0 0.65em;
}
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
    margin: 0 0 0.7em;
    padding-left: 1.75em;
}
.markdown-body :deep(li + li) {
    margin-top: 0.3em;
}
.markdown-body :deep(a) {
    color: #2875d2;
    text-decoration: none;
}
.markdown-body :deep(a:hover) {
    text-decoration: underline;
}
.markdown-body :deep(blockquote) {
    margin: 0 0 1em;
    padding: 0.65em 1em;
    border-left: 4px solid #8fb5e8;
    background: #f3f7fc;
    color: #5f6b7a;
}
.markdown-body :deep(code) {
    padding: 0.15em 0.38em;
    border-radius: 4px;
    background: #edf1f6;
    color: #c7254e;
    font-family: Consolas, "SFMono-Regular", monospace;
    font-size: 0.92em;
}
.markdown-body :deep(pre) {
    margin: 0 0 0.8em;
    padding: 11px 14px;
    overflow: auto;
    border: 1px solid #e1e7ef;
    border-radius: 7px;
    background: #f4f7fb;
    color: #344054;
    line-height: 1.55;
}
.markdown-body :deep(pre code) {
    padding: 0;
    background: transparent;
    color: inherit;
}
.markdown-body :deep(table) {
    width: 100%;
    margin-bottom: 1em;
    border-collapse: collapse;
}
.markdown-body :deep(th),
.markdown-body :deep(td) {
    padding: 8px 10px;
    border: 1px solid #dfe5ed;
    text-align: left;
}
.markdown-body :deep(th) {
    background: #f3f6fa;
    font-weight: 650;
}
.markdown-body :deep(hr) {
    height: 1px;
    margin: 0.9em 0;
    border: 0;
    background: #dfe5ed;
}
.markdown-body :deep(img) {
    max-width: 100%;
    height: auto;
}
.markdown-body :deep(.markdown-empty) {
    color: #98a2b3;
}
.audit-name {
    margin-bottom: 16px;
    font-weight: 600;
}
.modal-actions {
    margin-top: 12px;
    text-align: right;
}
.unpublish-wrap {
    box-sizing: border-box;
    padding: 12px 20px;
    font-size: 15px;
}
.selected-user {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-right: 12px;
}
.selected-user .va-icon {
    cursor: pointer;
}
.audit-summary {
    min-height: 0;
    flex: 1;
    margin-bottom: 12px;
    padding: 12px 18px;
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid #e4eaf2;
    border-radius: 10px;
    background: #f8fafc;
}
.audit-summary-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 9px;
    border-bottom: 1px solid #e7ebf0;
}
.skill-avatar.small {
    flex-basis: 42px;
    width: 42px;
    height: 42px;
    border-radius: 9px;
    font-size: 14px;
}
.audit-summary-head > div:nth-child(2) {
    min-width: 0;
    flex: 1;
}
.audit-summary-head h3 {
    margin: 0;
    color: #25324b;
    font-size: 20px;
    line-height: 28px;
}
.audit-summary-head p {
    max-width: 100%;
    margin: 4px 0 0;
    overflow: hidden;
    color: #778196;
    font-family: Consolas, monospace;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.audit-facts {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 7px 18px;
    padding-top: 9px;
}
.audit-facts div {
    min-width: 0;
}
.audit-facts .audit-fact-full {
    grid-column: 1 / -1;
}
.audit-facts label {
    display: block;
    margin-bottom: 5px;
    color: #98a2b3;
    font-size: 14px;
}
.audit-facts strong {
    color: #475467;
    font-size: 15px;
    font-weight: 500;
    line-height: 23px;
    overflow-wrap: anywhere;
}
.audit-release-notes {
    white-space: pre-wrap;
}
.opinion-label {
    display: block;
    margin-bottom: 7px;
    color: #344054;
    font-size: 14px;
    font-weight: 600;
}
.audit-log-wrap {
    padding: 18px 22px;
}
.empty-log {
    padding: 80px 0;
    color: #98a2b3;
    text-align: center;
}
.audit-log-item {
    position: relative;
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 12px;
    padding-bottom: 24px;
}
.audit-log-item:not(:last-child)::before {
    position: absolute;
    top: 14px;
    bottom: 0;
    left: 5px;
    width: 1px;
    background: #dfe5ed;
    content: "";
}
.log-dot {
    position: relative;
    z-index: 1;
    width: 11px;
    height: 11px;
    margin-top: 4px;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 0 0 2px currentColor;
    background: currentColor;
}
.log-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}
.log-head strong {
    color: #25324b;
    font-size: 16px;
}
.log-head time {
    color: #98a2b3;
    font-size: 13px;
}
.log-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 6px;
    color: #667085;
    font-size: 13px;
}
.log-content p {
    margin: 10px 0 0;
    padding: 10px 12px;
    border-radius: 6px;
    background: #f5f7fa;
    color: #475467;
    font-size: 14px;
    line-height: 22px;
}
@media (max-width: 820px) {
    .detail-columns {
        grid-template-columns: 1fr;
    }
    .info-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .info-item-span-2 {
        grid-column: 1 / -1;
    }
    .detail-hero {
        padding: 16px;
    }
    .skill-avatar {
        display: none;
    }
}
</style>
